import { Controller, useFormContext } from 'react-hook-form';

import { Input } from 'components/atoms';
import CatalogSelect from 'components/molecules/CatalogSelect/CatalogSelect';
import { useGetEntityQuery, useGetPicklistQuery } from 'services/sap';
import { catalogoLenguage, catalogoPersonal } from 'utils/helpers/dictionaries';

import { IAdminPanelForm } from '../AdminPanelForm.schema';
import { useOrganizationalSubmit } from '../useOrganizationalSubmit';

import ChapterSelect from './SelectContainers/ChapterSelect';
import SpecialitySelect from './SelectContainers/SpecialitySelect';
import TechnicalLevelSelect from './SelectContainers/TechnicalLevelSelect';
import TechnologySelect from './SelectContainers/TechnologySelect';

import styles from './OrganizationSection.module.scss';

interface OrganizationSectionProps {
 
  isLocked?: boolean;
 
  areOptionalFieldsEnabled?: boolean;
}

export default function OrganizationSection({
  isLocked = false,
  areOptionalFieldsEnabled = true,
}: Readonly<OrganizationSectionProps>) {

  const { control, getValues, setValue, trigger } = useFormContext<IAdminPanelForm>();

  const { data: chapter, isLoading: isChapterLoading } = useGetEntityQuery<{
    data: { name: string; externalCode: string }[];
    isLoading: boolean;
  }>({
    catalogName: catalogoPersonal.FOJobFunction,
    select: 'name,externalCode'
  });

  const { data: technicalLevel, isLoading: isTechnicalLevelLoading } = useGetPicklistQuery<{
    data: { label_es_ES: string; externalCode: string }[];
    isLoading: boolean;
  }>({
    catalogName: catalogoPersonal.Seniority,
    languages: [catalogoLenguage.ES, catalogoLenguage.externalCode],
  });

  const { data: speciality1, isLoading: isSpeciality1Loading } = useGetEntityQuery<{
    data: { externalName: string; externalCode: string }[];
    isLoading: boolean;
  }>({
    catalogName: catalogoPersonal.technicalLevel,
    select: 'externalName,externalCode',
    filter: `cust_chapter/name eq '${getValues().growInformation[0].chapter}'`
  });

  const { data: speciality2, isLoading: isSpeciality2Loading } = useGetEntityQuery<{
    data: { externalName: string; externalCode: string }[];
    isLoading: boolean;
  }>({
    catalogName: catalogoPersonal.technicalLevel,
    select: 'externalName,externalCode',
    filter: `cust_chapter/name eq '${getValues().growInformation[1].chapter}'`
  });

  const { data: technologyLevel1, isLoading: isTechnologyLevel1Loading } = useGetEntityQuery<{
    data: { externalName: string; externalCode: string }[];
    isLoading: boolean;
  }>({
    catalogName: catalogoPersonal.cust_Teconologia,
    select: 'externalName,externalCode,cust_Seniority_Level_Especialidad/name',
    filter: `cust_Seniority_Level_Especialidad/externalName eq '${getValues().growInformation[0].specialty}'`
  });

  const { data: technologyLevel2, isLoading: isTechnologyLevel2Loading } = useGetEntityQuery<{
    data: { externalName: string; externalCode: string }[];
    isLoading: boolean;
  }>({
    catalogName: catalogoPersonal.cust_Teconologia,
    select: 'externalName,externalCode,cust_Seniority_Level_Especialidad/name',
    filter: `cust_Seniority_Level_Especialidad/externalName eq '${getValues().growInformation[1].specialty}'`
  });

  const submitData = useOrganizationalSubmit();

  return (
    <div className={styles.OrganizationSection}>
      <h2 className={styles.OrganizationSection__Title}>Información organizacional</h2>
      <div className={styles.OrganizationSection__Grid}>
        <Controller
          control={control}
          name='organizationalInformation.division'
          render={({ field: { onChange, value, name, ...field }, fieldState: { error } }) => (
            <CatalogSelect
              catalog={catalogoPersonal.FODivision}
              select={catalogoLenguage.name}
              value={{
                value,
                label: value ?? '',
              }}
              handleChange={(value) => {
                onChange(value);
                submitData();
              }}
              error={error !== undefined}
              textLabel='División'
              htmlForLabel='División'
              subtext={error?.message}
              name={name}
              isEntity={true}
              disabled={isLocked}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name='organizationalInformation.department'
          render={({ field: { onChange, value, name, ...field }, fieldState: { error } }) => (
            <CatalogSelect
              catalog={catalogoPersonal.FODepartment}
              select={catalogoLenguage.name}
              value={{
                value,
                label: value ?? '',
              }}
              handleChange={(value) => {
                onChange(value);
                submitData();
              }}
              error={error !== undefined}
              textLabel='Departamento'
              htmlForLabel='Departamento'
              subtext={error?.message}
              name={name}
              isEntity={true}
              disabled={isLocked}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name='growInformation.0.chapter'
          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
            <ChapterSelect
              idPosition={0}
              textLabel='Chapter 1'
              htmlForLabel='Chapter 1'
              subtext={error?.message}
              error={error !== undefined}
              chapter={chapter}
              isLoading={isChapterLoading}
              disabled={isLocked}
              value={{ value, label: value ?? '' }}
              handleChange={({ value, externalCode }) => {
                let { growInformation } = getValues();
                if (value != growInformation[0].chapter) {
                  setValue('growInformation.0.idSpecialtySap', '');
                  setValue('growInformation.0.specialty', '');
                  setValue('growInformation.0.idTechnologySap', '');
                  setValue('growInformation.0.technology', '');
                }
                setValue('growInformation.0.idChapterSap', externalCode);
                trigger('growInformation.0.specialty');
                trigger('growInformation.0.technology');
                onChange(value);
                submitData();
              }}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name='growInformation.1.chapter'
          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
            <ChapterSelect
              idPosition={1}
              chapter={chapter}
              isLoading={isChapterLoading}
              value={{
                value,
                label: value ?? '',
              }}
              handleChange={({ value, externalCode }) => {
                let { growInformation } = getValues();
                if (value != growInformation[1].chapter) {
                  setValue('growInformation.1.idSpecialtySap', '');
                  setValue('growInformation.1.specialty', '');
                  setValue('growInformation.1.idTechnologySap', '');
                  setValue('growInformation.1.technology', '');
                }
                setValue('growInformation.1.idChapterSap', externalCode);
                onChange(value);
                submitData();
              }}
              disabled={!areOptionalFieldsEnabled}
              error={error !== undefined}
              textLabel='Chapter 2 (opcional)'
              htmlForLabel='Chapter 2 (opcional)1'
              subtext={error?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name='growInformation.0.specialty'
          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
            <SpecialitySelect
              idPosition={0}
              chapter={speciality1}
              isLoading={isSpeciality1Loading}
              value={{
                value,
                label: value ?? '',
              }}
              handleChange={({ value, externalCode }) => {
                let { growInformation } = getValues();
                if (value != growInformation[0].specialty) {
                  setValue('growInformation.0.idTechnologySap', '');
                  setValue('growInformation.0.technology', '');
                }
                setValue('growInformation.0.idSpecialtySap', externalCode);
                trigger('growInformation.0.technology');
                onChange(value);
                submitData();
              }}
              disabled={isLocked || (speciality1 && speciality1.length === 0)}
              error={error !== undefined}
              textLabel='Especialidad 1'
              htmlForLabel='Especialidad 1'
              subtext={error?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name='growInformation.1.specialty'
          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
            <SpecialitySelect
              idPosition={1}
              chapter={speciality2}
              isLoading={isSpeciality2Loading}
              value={{
                value,
                label: value ?? '',
              }}
              handleChange={({ value, externalCode }) => {
                let { growInformation } = getValues();
                if (value != growInformation[1].specialty) {
                  setValue('growInformation.1.idTechnologySap', '');
                  setValue('growInformation.1.technology', '');
                }
                setValue('growInformation.1.idSpecialtySap', externalCode);
                onChange(value);
                submitData();
              }}
              disabled={!areOptionalFieldsEnabled || (speciality2 && speciality2.length === 0)}
              error={error !== undefined}
              textLabel='Especialidad 2 (opcional)'
              htmlForLabel='Especialidad 2 (opcional)'
              subtext={error?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name='growInformation.0.technology'
          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
            <TechnologySelect
              idPosition={0}
              chapter={technologyLevel1}
              isLoading={isTechnologyLevel1Loading}
              value={{
                value,
                label: value ?? '',
              }}
              handleChange={({ value, externalCode }) => {
                setValue('growInformation.0.idTechnologySap', externalCode);
                onChange(value);
                submitData();
              }}
              disabled={isLocked || (technologyLevel1 && technologyLevel1.length === 0)}
              error={error !== undefined}
              textLabel='Tecnología 1 (opcional)'
              htmlForLabel='Tecnología 1 (opcional)'
              subtext={error?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name='growInformation.1.technology'
          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
            <TechnologySelect
              idPosition={1}
              chapter={technologyLevel2}
              isLoading={isTechnologyLevel2Loading}
              value={{
                value,
                label: value ?? '',
              }}
              handleChange={({ value, externalCode }) => {
                setValue('growInformation.1.idTechnologySap', externalCode);
                onChange(value);
                submitData();
              }}
              disabled={!areOptionalFieldsEnabled || (technologyLevel2 && technologyLevel2.length === 0)}
              error={error !== undefined}
              textLabel='Tecnología 2 (opcional)'
              htmlForLabel='Tecnología 2 (opcional)'
              subtext={error?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name='growInformation.0.technicaLevel'
          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
            <TechnicalLevelSelect
              chapter={technicalLevel}
              isLoading={isTechnicalLevelLoading}
              value={{
                value,
                label: value ?? '',
              }}
              handleChange={({ value }) => {
                onChange(value);
                submitData();
              }}
              disabled={isLocked}
              error={error !== undefined}
              textLabel='Nivel técnico 1 (opcional)'
              htmlForLabel='Nivel técnico 1 (opcional)'
              subtext={error?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name='growInformation.1.technicaLevel'
          render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
            <TechnicalLevelSelect
              value={{
                value,
                label: value ?? '',
              }}
              chapter={technicalLevel}
              isLoading={isTechnicalLevelLoading}
              handleChange={({ value, externalCode }) => {
                setValue('growInformation.1.idTechnicalLevelSap', externalCode);
                onChange(value);
                submitData();
              }}
              disabled={!areOptionalFieldsEnabled}
              error={error !== undefined}
              textLabel='Nivel técnico 2 (opcional)'
              htmlForLabel='Nivel técnico 2 (opcional)'
              subtext={error?.message}
              {...field}
            />
          )}
        />
        <div className={styles.OrganizationSection__ReferredContainer}>
          <Controller
            control={control}
            name='organizationalInformation.referred'
            render={({ field, fieldState: { error } }) => (
              <Input
                error={error !== undefined}
                id='referred'
                label='Referido por (opcional)'
                subtext={error?.message}
                disabled={!areOptionalFieldsEnabled}
                {...field}
                onBlur={() => {
                  field.onBlur();
                  submitData();
                }}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
